/**
 * Export Utilities for Professional Document Generation
 * Uses 'docx' library for Word and 'jspdf' for PDF
 */

import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle, TableCell, TableRow, Table, WidthType } from 'docx';
import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';

/**
 * Strip HTML tags from string
 */
const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

/**
 * Export Soal to Professional Word Document
 * @param {Array} soalData - Array of question objects
 * @param {Object} metadata - Document metadata (topic, level, assessmentType, etc.)
 * @param {string} fileName - Output file name
 */
export const exportSoalToWord = async (soalData, metadata = {}, fileName = 'soal') => {
    if (!soalData || soalData.length === 0) {
        alert('Tidak ada soal untuk diekspor.');
        return;
    }

    try {
        const today = new Date().toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });

        // Build document sections
        const children = [];

        // === HEADER SECTION ===
        children.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: 'SOAL UJIAN',
                        bold: true,
                        size: 32, // 16pt
                        font: 'Times New Roman'
                    })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 100 }
            })
        );

        // Add topic/title
        if (metadata.topik) {
            children.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: metadata.topik.toUpperCase(),
                            bold: true,
                            size: 28, // 14pt
                            font: 'Times New Roman'
                        })
                    ],
                    alignment: AlignmentType.CENTER,
                    spacing: { after: 100 }
                })
            );
        }

        // Metadata line
        const metaItems = [];
        if (metadata.jenisSoal) metaItems.push(`Jenis: ${metadata.jenisSoal}`);
        if (metadata.tingkat) metaItems.push(`Tingkat: ${metadata.tingkat}`);
        if (metadata.assessmentType) metaItems.push(`Penilaian: ${metadata.assessmentType}`);

        if (metaItems.length > 0) {
            children.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: metaItems.join(' | '),
                            size: 22,
                            font: 'Times New Roman',
                            italics: true
                        })
                    ],
                    alignment: AlignmentType.CENTER,
                    spacing: { after: 100 }
                })
            );
        }

        // Date
        children.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: `Tanggal: ${today}`,
                        size: 20,
                        font: 'Times New Roman'
                    })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 300 }
            })
        );

        // Separator line
        children.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: '═'.repeat(80),
                        size: 20,
                        font: 'Times New Roman'
                    })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 300 }
            })
        );

        // === QUESTIONS SECTION ===
        const answerKeys = []; // Store answers for key section

        soalData.forEach((item, index) => {
            const questionNumber = index + 1;
            const questionText = stripHtml(item.soal);

            // Question number and text
            children.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: `${questionNumber}. `,
                            bold: true,
                            size: 24,
                            font: 'Times New Roman'
                        }),
                        new TextRun({
                            text: questionText,
                            size: 24,
                            font: 'Times New Roman'
                        })
                    ],
                    spacing: { before: 200, after: 100 }
                })
            );

            // Answer options for multiple choice
            if (item.pilihan && typeof item.pilihan === 'object') {
                const options = ['A', 'B', 'C', 'D', 'E'];
                Object.entries(item.pilihan).forEach(([key, value]) => {
                    const optionText = stripHtml(value);
                    const isCorrect = key === item.jawaban;

                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: `     ${key}. `,
                                    bold: true,
                                    size: 22,
                                    font: 'Times New Roman'
                                }),
                                new TextRun({
                                    text: optionText,
                                    size: 22,
                                    font: 'Times New Roman'
                                })
                            ],
                            spacing: { after: 50 }
                        })
                    );
                });

                // Store answer for key section
                answerKeys.push({
                    number: questionNumber,
                    answer: item.jawaban || '-'
                });
            } else {
                // Essay/short answer - show as blank line for answer
                children.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: '     Jawaban: ________________________________________',
                                size: 22,
                                font: 'Times New Roman',
                                italics: true
                            })
                        ],
                        spacing: { after: 100 }
                    })
                );

                // Store essay answer
                answerKeys.push({
                    number: questionNumber,
                    answer: stripHtml(item.jawaban) || 'Lihat kunci jawaban essay'
                });
            }

            // Add spacing between questions
            children.push(
                new Paragraph({
                    children: [],
                    spacing: { after: 150 }
                })
            );
        });

        // === ANSWER KEY SECTION ===
        children.push(
            new Paragraph({
                children: [],
                spacing: { before: 400 }
            })
        );

        children.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: '═'.repeat(80),
                        size: 20,
                        font: 'Times New Roman'
                    })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 }
            })
        );

        children.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: 'KUNCI JAWABAN',
                        bold: true,
                        size: 28,
                        font: 'Times New Roman'
                    })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 }
            })
        );

        // Answer keys in compact format
        const answersPerRow = 5;
        for (let i = 0; i < answerKeys.length; i += answersPerRow) {
            const rowAnswers = answerKeys.slice(i, i + answersPerRow);
            const answerTexts = rowAnswers.map(ak => `${ak.number}. ${ak.answer}`).join('     ');

            children.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: answerTexts,
                            size: 22,
                            font: 'Times New Roman'
                        })
                    ],
                    spacing: { after: 50 }
                })
            );
        }

        // Create document
        const doc = new Document({
            sections: [{
                properties: {
                    page: {
                        margin: {
                            top: 1440, // 1 inch
                            right: 1440,
                            bottom: 1440,
                            left: 1440
                        }
                    }
                },
                children: children
            }]
        });

        // Generate and save
        const blob = await Packer.toBlob(doc);
        const finalFileName = `${fileName}_${new Date().toISOString().split('T')[0]}.docx`;
        saveAs(blob, finalFileName);

        console.log('✅ Word document exported successfully');
        return true;

    } catch (error) {
        console.error('❌ Error exporting to Word:', error);
        alert(`Gagal mengekspor ke Word: ${error.message}`);
        return false;
    }
};

/**
 * Export Soal to Professional PDF Document
 * @param {Array} soalData - Array of question objects  
 * @param {Object} metadata - Document metadata
 * @param {string} fileName - Output file name
 */
export const exportSoalToPdf = async (soalData, metadata = {}, fileName = 'soal') => {
    if (!soalData || soalData.length === 0) {
        alert('Tidak ada soal untuk diekspor.');
        return;
    }

    try {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        const contentWidth = pageWidth - (margin * 2);
        let y = margin;
        let pageNumber = 1;

        const today = new Date().toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });

        // Helper: Add new page if needed
        const checkPageBreak = (requiredHeight = 30) => {
            if (y + requiredHeight > pageHeight - margin - 10) {
                // Add page number before new page
                pdf.setFontSize(10);
                pdf.setTextColor(128, 128, 128);
                pdf.text(`Halaman ${pageNumber}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

                pdf.addPage();
                pageNumber++;
                y = margin;
                return true;
            }
            return false;
        };

        // Helper: Add wrapped text
        const addWrappedText = (text, x, fontSize, isBold = false, indent = 0) => {
            pdf.setFontSize(fontSize);
            pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
            pdf.setTextColor(0, 0, 0);

            const lines = pdf.splitTextToSize(text, contentWidth - indent);
            lines.forEach((line, idx) => {
                checkPageBreak(8);
                pdf.text(line, x + (idx > 0 ? indent : 0), y);
                y += fontSize * 0.4;
            });
            return lines.length;
        };

        // === HEADER ===
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text('SOAL UJIAN', pageWidth / 2, y, { align: 'center' });
        y += 10;

        if (metadata.topik) {
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(metadata.topik.toUpperCase(), pageWidth / 2, y, { align: 'center' });
            y += 8;
        }

        // Metadata
        const metaItems = [];
        if (metadata.jenisSoal) metaItems.push(`Jenis: ${metadata.jenisSoal}`);
        if (metadata.tingkat) metaItems.push(`Tingkat: ${metadata.tingkat}`);
        if (metadata.assessmentType) metaItems.push(`Penilaian: ${metadata.assessmentType}`);

        if (metaItems.length > 0) {
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(80, 80, 80);
            pdf.text(metaItems.join(' | '), pageWidth / 2, y, { align: 'center' });
            y += 6;
        }

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 80, 80);
        pdf.text(`Tanggal: ${today}`, pageWidth / 2, y, { align: 'center' });
        y += 10;

        // Separator
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.5);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 15;

        // === QUESTIONS ===
        const answerKeys = [];

        soalData.forEach((item, index) => {
            const questionNumber = index + 1;
            const questionText = stripHtml(item.soal);

            checkPageBreak(40);

            // Question number and text
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0);

            const qPrefix = `${questionNumber}. `;
            pdf.text(qPrefix, margin, y);

            const prefixWidth = pdf.getTextWidth(qPrefix);
            pdf.setFont('helvetica', 'normal');

            const lines = pdf.splitTextToSize(questionText, contentWidth - prefixWidth);
            lines.forEach((line, idx) => {
                if (idx > 0) checkPageBreak(6);
                pdf.text(line, margin + (idx === 0 ? prefixWidth : 8), y);
                y += 5;
            });
            y += 3;

            // Answer options
            if (item.pilihan && typeof item.pilihan === 'object') {
                Object.entries(item.pilihan).forEach(([key, value]) => {
                    checkPageBreak(8);
                    const optionText = stripHtml(value);

                    pdf.setFontSize(10);
                    pdf.setFont('helvetica', 'bold');
                    pdf.text(`     ${key}. `, margin, y);

                    const optWidth = pdf.getTextWidth(`     ${key}. `);
                    pdf.setFont('helvetica', 'normal');

                    const optLines = pdf.splitTextToSize(optionText, contentWidth - optWidth - 5);
                    optLines.forEach((line, idx) => {
                        pdf.text(line, margin + (idx === 0 ? optWidth : 15), y);
                        if (idx < optLines.length - 1) y += 4;
                    });
                    y += 5;
                });

                answerKeys.push({
                    number: questionNumber,
                    answer: item.jawaban || '-'
                });
            } else {
                // Essay
                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'italic');
                pdf.text('     Jawaban: ________________________________________', margin, y);
                y += 5;

                answerKeys.push({
                    number: questionNumber,
                    answer: 'Essay'
                });
            }

            y += 8; // Space between questions
        });

        // === ANSWER KEY SECTION ===
        checkPageBreak(60);
        y += 10;

        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.5);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 8;

        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('KUNCI JAWABAN', pageWidth / 2, y, { align: 'center' });
        y += 10;

        // Answer keys in compact format
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');

        const answersPerRow = 5;
        for (let i = 0; i < answerKeys.length; i += answersPerRow) {
            checkPageBreak(8);
            const rowAnswers = answerKeys.slice(i, i + answersPerRow);
            const answerText = rowAnswers.map(ak => `${ak.number}. ${ak.answer}`).join('     ');
            pdf.text(answerText, margin, y);
            y += 6;
        }

        // Final page number
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Halaman ${pageNumber}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

        // Save
        const finalFileName = `${fileName}_${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(finalFileName);

        console.log('✅ PDF document exported successfully');
        return true;

    } catch (error) {
        console.error('❌ Error exporting to PDF:', error);
        alert(`Gagal mengekspor ke PDF: ${error.message}`);
        return false;
    }
};

/**
 * Export Modul Ajar/RPP to Professional Word Document
 * @param {Object} modulData - Generated modul ajar content
 * @param {Object} metadata - Document metadata (mataPelajaran, fase, topik, etc.)
 * @param {Object} profileData - Teacher profile data (namaGuru, namaSekolah, nip)
 * @param {string} fileName - Output file name
 */
export const exportModulAjarToWord = async (modulData, metadata = {}, profileData = {}, fileName = 'modul_ajar') => {
    if (!modulData) {
        alert('Tidak ada modul ajar untuk diekspor.');
        return;
    }

    try {
        const today = new Date().toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });

        // Helper function to clean markdown formatting
        const cleanMarkdown = (text) => {
            if (!text) return '';
            return text
                // Remove bold markdown **text** or __text__
                .replace(/\*\*([^*]+)\*\*/g, '$1')
                .replace(/__([^_]+)__/g, '$1')
                // Remove italic markdown *text* or _text_
                .replace(/\*([^*]+)\*/g, '$1')
                .replace(/_([^_]+)_/g, '$1')
                // Remove headers # ## ###
                .replace(/^#{1,6}\s*/gm, '')
                // Remove horizontal rules
                .replace(/^[-=]{3,}$/gm, '')
                .replace(/^[─═]{3,}$/gm, '')
                // Remove table separators like |---|---|
                .replace(/^\|[-:|\s]+\|$/gm, '')
                // Remove emoji characters (optional, keep common ones)
                .replace(/[📋📌🎯📚👥📊📅📝📎✅⚠️═]/g, '')
                // Clean up pipe characters from tables
                .replace(/^\|/gm, '')
                .replace(/\|$/gm, '')
                .replace(/\|/g, ' - ')
                // Remove excessive whitespace
                .replace(/\s{3,}/g, '  ')
                .trim();
        };

        // Helper function to detect line type
        const getLineType = (line) => {
            const trimmed = line.trim();

            // Skip empty lines or separator lines
            if (!trimmed || /^[-═─=]{3,}$/.test(trimmed)) {
                return { type: 'skip' };
            }

            // Main section headers (numbered like "1. Identitas Modul" or "1. IDENTITAS MODUL")
            if (/^\d+\.\s*[A-Z][a-zA-Z\s]+$/.test(trimmed) && trimmed.length < 80) {
                return { type: 'mainHeader', text: cleanMarkdown(trimmed) };
            }

            // Sub headers (A. B. C. or all caps)
            if (/^[A-Z]\.\s/.test(trimmed) || /^[A-Z]{3,}[A-Z\s:]+$/.test(trimmed)) {
                return { type: 'subHeader', text: cleanMarkdown(trimmed) };
            }

            // Bullet points
            if (/^[-•*✓]\s/.test(trimmed)) {
                return { type: 'bullet', text: cleanMarkdown(trimmed.replace(/^[-•*✓]\s/, '')) };
            }

            // Numbered list items (but not main headers)
            if (/^\d+\)\s/.test(trimmed) || (/^\d+\.\s/.test(trimmed) && !/^[0-9]+\.\s*[A-Z]{2,}/.test(trimmed))) {
                return { type: 'numbered', text: cleanMarkdown(trimmed) };
            }

            // Key-value pairs (Label: Value or Label : Value)
            const kvMatch = trimmed.match(/^([^:]+):\s*(.+)$/);
            if (kvMatch && kvMatch[1].length < 40) {
                return {
                    type: 'keyValue',
                    label: cleanMarkdown(kvMatch[1].trim()),
                    value: cleanMarkdown(kvMatch[2].trim())
                };
            }

            // Regular paragraph text
            return { type: 'paragraph', text: cleanMarkdown(trimmed) };
        };

        const children = [];

        // === KOP SEKOLAH / HEADER ===
        children.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: (profileData.namaSekolah || 'NAMA SEKOLAH').toUpperCase(),
                        bold: true,
                        size: 32,
                        font: 'Times New Roman'
                    })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 50 }
            })
        );

        children.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: profileData.alamatSekolah || 'Alamat Sekolah',
                        size: 20,
                        font: 'Times New Roman'
                    })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 150 }
            })
        );

        // Garis pembatas kop
        children.push(
            new Paragraph({
                border: {
                    bottom: { style: 'double', size: 6, color: '000000' }
                },
                spacing: { after: 300 }
            })
        );

        // === JUDUL DOKUMEN ===
        children.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: 'MODUL AJAR / RENCANA PELAKSANAAN PEMBELAJARAN (RPP)',
                        bold: true,
                        size: 28,
                        font: 'Times New Roman'
                    })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 100 }
            })
        );

        children.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: 'KURIKULUM MERDEKA',
                        bold: true,
                        size: 24,
                        font: 'Times New Roman'
                    })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 300 }
            })
        );

        // === KONTEN MODUL AJAR ===
        // Note: Identitas Modul sudah termasuk dalam generated content dari AI

        // === KONTEN MODUL AJAR ===
        const contentText = typeof modulData === 'string' ? modulData : JSON.stringify(modulData, null, 2);
        const lines = contentText.split('\n');

        lines.forEach(line => {
            const lineInfo = getLineType(line);

            switch (lineInfo.type) {
                case 'skip':
                    // Add small spacing for visual separation
                    children.push(
                        new Paragraph({
                            children: [],
                            spacing: { after: 100 }
                        })
                    );
                    break;

                case 'mainHeader':
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: lineInfo.text, // Use original text (Title Case)
                                    bold: true,
                                    size: 24,
                                    font: 'Times New Roman'
                                })
                            ],
                            spacing: { before: 300, after: 150 },
                            shading: {
                                type: 'clear',
                                fill: 'F2F2F2'
                            }
                        })
                    );
                    break;

                case 'subHeader':
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: lineInfo.text,
                                    bold: true,
                                    size: 22,
                                    font: 'Times New Roman'
                                })
                            ],
                            spacing: { before: 200, after: 100 }
                        })
                    );
                    break;

                case 'bullet':
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: '• ' + lineInfo.text,
                                    size: 22,
                                    font: 'Times New Roman'
                                })
                            ],
                            indent: { left: 720 },
                            spacing: { after: 50 }
                        })
                    );
                    break;

                case 'numbered':
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: lineInfo.text,
                                    size: 22,
                                    font: 'Times New Roman'
                                })
                            ],
                            indent: { left: 720 },
                            spacing: { after: 50 }
                        })
                    );
                    break;

                case 'keyValue':
                    children.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: lineInfo.label,
                                    bold: true,
                                    size: 22,
                                    font: 'Times New Roman'
                                }),
                                new TextRun({
                                    text: `: ${lineInfo.value}`,
                                    size: 22,
                                    font: 'Times New Roman'
                                })
                            ],
                            indent: { left: 360 },
                            spacing: { after: 50 }
                        })
                    );
                    break;

                case 'paragraph':
                default:
                    if (lineInfo.text && lineInfo.text.length > 0) {
                        children.push(
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: lineInfo.text,
                                        size: 22,
                                        font: 'Times New Roman'
                                    })
                                ],
                                indent: { left: 360 },
                                spacing: { after: 50 }
                            })
                        );
                    }
                    break;
            }
        });

        // === TANDA TANGAN ===
        children.push(
            new Paragraph({
                children: [],
                spacing: { before: 500 }
            })
        );

        // Create table for signatures
        const signatureTable = new Table({
            width: { size: 100, type: 'pct' },
            borders: {
                top: { style: 'none' },
                bottom: { style: 'none' },
                left: { style: 'none' },
                right: { style: 'none' },
                insideHorizontal: { style: 'none' },
                insideVertical: { style: 'none' }
            },
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: 'Mengetahui,',
                                            size: 22,
                                            font: 'Times New Roman'
                                        })
                                    ]
                                })
                            ],
                            width: { size: 50, type: 'pct' },
                            borders: {
                                top: { style: 'none' },
                                bottom: { style: 'none' },
                                left: { style: 'none' },
                                right: { style: 'none' }
                            }
                        }),
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: `${metadata.kota || 'Kota'}, ${today}`,
                                            size: 22,
                                            font: 'Times New Roman'
                                        })
                                    ],
                                    alignment: AlignmentType.LEFT
                                })
                            ],
                            width: { size: 50, type: 'pct' },
                            borders: {
                                top: { style: 'none' },
                                bottom: { style: 'none' },
                                left: { style: 'none' },
                                right: { style: 'none' }
                            }
                        })
                    ]
                }),
                new TableRow({
                    children: [
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: 'Kepala Sekolah,',
                                            size: 22,
                                            font: 'Times New Roman'
                                        })
                                    ]
                                })
                            ],
                            borders: {
                                top: { style: 'none' },
                                bottom: { style: 'none' },
                                left: { style: 'none' },
                                right: { style: 'none' }
                            }
                        }),
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: 'Guru Mata Pelajaran,',
                                            size: 22,
                                            font: 'Times New Roman'
                                        })
                                    ]
                                })
                            ],
                            borders: {
                                top: { style: 'none' },
                                bottom: { style: 'none' },
                                left: { style: 'none' },
                                right: { style: 'none' }
                            }
                        })
                    ]
                }),
                new TableRow({
                    children: [
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [],
                                    spacing: { after: 800 }
                                })
                            ],
                            borders: {
                                top: { style: 'none' },
                                bottom: { style: 'none' },
                                left: { style: 'none' },
                                right: { style: 'none' }
                            }
                        }),
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [],
                                    spacing: { after: 800 }
                                })
                            ],
                            borders: {
                                top: { style: 'none' },
                                bottom: { style: 'none' },
                                left: { style: 'none' },
                                right: { style: 'none' }
                            }
                        })
                    ]
                }),
                new TableRow({
                    children: [
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: profileData.namaKepalaSekolah || '________________________',
                                            bold: true,
                                            size: 22,
                                            font: 'Times New Roman',
                                            underline: profileData.namaKepalaSekolah ? {} : undefined
                                        })
                                    ]
                                }),
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: `NIP. ${profileData.nipKepalaSekolah || '________________________'}`,
                                            size: 20,
                                            font: 'Times New Roman'
                                        })
                                    ]
                                })
                            ],
                            borders: {
                                top: { style: 'none' },
                                bottom: { style: 'none' },
                                left: { style: 'none' },
                                right: { style: 'none' }
                            }
                        }),
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: profileData.namaGuru || '________________________',
                                            bold: true,
                                            size: 22,
                                            font: 'Times New Roman',
                                            underline: profileData.namaGuru ? {} : undefined
                                        })
                                    ]
                                }),
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: `NIP. ${profileData.nip || '________________________'}`,
                                            size: 20,
                                            font: 'Times New Roman'
                                        })
                                    ]
                                })
                            ],
                            borders: {
                                top: { style: 'none' },
                                bottom: { style: 'none' },
                                left: { style: 'none' },
                                right: { style: 'none' }
                            }
                        })
                    ]
                })
            ]
        });

        children.push(signatureTable);

        // Create document
        const doc = new Document({
            sections: [{
                properties: {
                    page: {
                        margin: {
                            top: 1440,
                            right: 1440,
                            bottom: 1440,
                            left: 1440
                        }
                    }
                },
                children: children
            }]
        });

        // Generate and save
        const blob = await Packer.toBlob(doc);
        const finalFileName = `${fileName}_${metadata.mataPelajaran || 'modul'}_${new Date().toISOString().split('T')[0]}.docx`;
        saveAs(blob, finalFileName);

        console.log('✅ Modul Ajar Word document exported successfully');
        return true;

    } catch (error) {
        console.error('❌ Error exporting Modul Ajar to Word:', error);
        alert(`Gagal mengekspor ke Word: ${error.message}`);
        return false;
    }
};

export default {
    exportSoalToWord,
    exportSoalToPdf,
    exportModulAjarToWord
};
